// src/lib/pendingTransaction.ts
// モバイルMetaMask対策: ペンディングトランザクション管理ユーティリティ
//
// 問題:
// - モバイルでMetaMaskを開くと別タブ/アプリに切り替わる
// - 元のタブがバックグラウンドになりサスペンドされる
// - MetaMaskで承認後、元のタブに戻るとJavaScriptの実行状態が不安定
// - waitForTransactionReceiptが正しく動作しない
//
// 解決策:
// - トランザクション送信後すぐにlocalStorageに保存
// - ページ復帰時にペンディングトランザクションを検証

import { publicClient } from '../contract';

// ペンディングトランザクションの有効期限（1時間）
const PENDING_TX_EXPIRY_MS = 60 * 60 * 1000;

// トランザクション種別
export type PendingTxType = 'free_mint' | 'transfer' | 'mint_modal' | 'stamp_reward' | 'tip';

// ペンディングトランザクションデータ
export interface PendingTxData {
  txHash: string;
  type: PendingTxType;
  userAddress: string;
  timestamp: number;
  // 追加のメタデータ（種別によって異なる）
  metadata: Record<string, any>;
}

// localStorageキーを生成
const getStorageKey = (type: PendingTxType, identifier: string): string => {
  return `gifterra_pending_tx_${type}_${identifier}`;
};

/**
 * ペンディングトランザクションを保存
 */
export const savePendingTx = (
  type: PendingTxType,
  identifier: string,
  txHash: string,
  userAddress: string,
  metadata: Record<string, any> = {}
): void => {
  try {
    const key = getStorageKey(type, identifier);
    const data: PendingTxData = {
      txHash,
      type,
      userAddress,
      timestamp: Date.now(),
      metadata,
    };
    localStorage.setItem(key, JSON.stringify(data));
  } catch (e) {
    console.error('[PendingTx] Failed to save:', e);
  }
};

/**
 * ペンディングトランザクションを取得
 */
export const getPendingTx = (
  type: PendingTxType,
  identifier: string
): PendingTxData | null => {
  try {
    const key = getStorageKey(type, identifier);
    const data = localStorage.getItem(key);
    if (!data) return null;

    const parsed = JSON.parse(data) as PendingTxData;

    // 有効期限チェック
    if (Date.now() - parsed.timestamp > PENDING_TX_EXPIRY_MS) {
      localStorage.removeItem(key);
      return null;
    }

    return parsed;
  } catch (e) {
    console.error('[PendingTx] Failed to get:', e);
    return null;
  }
};

/**
 * ペンディングトランザクションをクリア
 */
export const clearPendingTx = (type: PendingTxType, identifier: string): void => {
  try {
    const key = getStorageKey(type, identifier);
    localStorage.removeItem(key);
  } catch (e) {
    console.error('[PendingTx] Failed to clear:', e);
  }
};

/**
 * ペンディングトランザクションを検証
 * @returns 成功した場合はreceipt、失敗・未確認の場合はnull
 */
export const verifyPendingTx = async (
  pendingTx: PendingTxData,
  timeoutMs: number = 60_000
): Promise<{
  success: boolean;
  receipt?: any;
  error?: string;
}> => {
  try {
    const receipt = await publicClient.waitForTransactionReceipt({
      hash: pendingTx.txHash as `0x${string}`,
      timeout: timeoutMs,
    });

    // 明示的に成功を確認（revertedだけでなく、successでないすべてのケースをエラーとして扱う）
    if (receipt.status !== 'success') {
      return {
        success: false,
        receipt,
        error: `トランザクションが失敗しました（ステータス: ${receipt.status}）`,
      };
    }

    return {
      success: true,
      receipt,
    };
  } catch (err: any) {
    console.error('[PendingTx] Verification failed:', err);

    // トランザクションが見つからない or タイムアウト
    if (err.message?.includes('not found') || err.message?.includes('timeout') || err.name === 'TimeoutError') {
      return {
        success: false,
        error: 'トランザクションの確認がタイムアウトしました。しばらく待ってからページを再読み込みしてください。',
      };
    }

    return {
      success: false,
      error: err.message || 'トランザクションの確認に失敗しました',
    };
  }
};

/**
 * 特定種別のすべてのペンディングトランザクションを取得
 */
export const getAllPendingTxByType = (type: PendingTxType): PendingTxData[] => {
  const results: PendingTxData[] = [];
  const prefix = `gifterra_pending_tx_${type}_`;

  try {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(prefix)) {
        const data = localStorage.getItem(key);
        if (data) {
          try {
            const parsed = JSON.parse(data) as PendingTxData;
            // 有効期限チェック
            if (Date.now() - parsed.timestamp <= PENDING_TX_EXPIRY_MS) {
              results.push(parsed);
            } else {
              // 期限切れは削除
              localStorage.removeItem(key);
            }
          } catch {
            // パース失敗は無視
          }
        }
      }
    }
  } catch (e) {
    console.error('[PendingTx] Failed to get all:', e);
  }

  return results;
};

/**
 * ERC721のトークンIDをレシートから取得
 */
export const extractTokenIdFromReceipt = (receipt: any): string | null => {
  if (!receipt.logs || receipt.logs.length === 0) return null;

  for (const log of receipt.logs) {
    // ERC721のTransferイベント: topics[3]にtokenIdがある
    if (log.topics && log.topics.length >= 4 && log.topics[3]) {
      try {
        return BigInt(log.topics[3]).toString();
      } catch {
        // パース失敗は無視
      }
    }
  }

  return null;
};

/**
 * visibilitychangeイベントでペンディングトランザクションを自動検証するフック
 * タブがアクティブに戻った時に自動で検証を再開する
 *
 * @param type - トランザクション種別
 * @param identifier - 一意の識別子
 * @param userAddress - ユーザーアドレス
 * @param onVerified - 検証成功時のコールバック
 * @param onFailed - 検証失敗時のコールバック
 * @param isAlreadyVerifying - 検証中フラグ（重複実行防止）
 */
export const setupVisibilityChangeHandler = (
  type: PendingTxType,
  identifier: string,
  userAddress: string,
  onVerified: (receipt: any, pendingTx: PendingTxData) => void,
  onFailed: (error: string, pendingTx: PendingTxData) => void,
  isAlreadyVerifying: () => boolean
): (() => void) => {
  const handleVisibilityChange = async () => {
    if (document.visibilityState !== 'visible') return;
    if (isAlreadyVerifying()) return;

    const pendingTx = getPendingTx(type, identifier);
    if (!pendingTx) return;
    if (pendingTx.userAddress.toLowerCase() !== userAddress.toLowerCase()) return;

    const result = await verifyPendingTx(pendingTx);

    if (result.success && result.receipt) {
      clearPendingTx(type, identifier);
      onVerified(result.receipt, pendingTx);
    } else if (result.error && !result.error.includes('timeout')) {
      clearPendingTx(type, identifier);
      onFailed(result.error, pendingTx);
    }
    // タイムアウトの場合はペンディングを残して次回再試行
  };

  document.addEventListener('visibilitychange', handleVisibilityChange);

  // クリーンアップ関数を返す
  return () => {
    document.removeEventListener('visibilitychange', handleVisibilityChange);
  };
};
